import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import Chip from "@material-ui/core/Chip";
import { Box, Tooltip } from "@material-ui/core";
import toastError from "../../errors/toastError";
import api from "../../services/api";

/**
 * Multi-select de conexões (whatsapps) — espelho do `QueueSelect`.
 *
 * Usado no UserModal para configurar QUAIS conexões um usuário (atendente)
 * pode usar para criar novos tickets / enviar mensagens iniciais.
 *
 * Lista TODAS as conexões da empresa (não só as do user atual) — quem está
 * editando precisa ver tudo para escolher. O backend valida no save que os
 * IDs pertencem à empresa correta (`sanitizeWhatsappIdsForCompany`).
 *
 * Props:
 *  - selectedWhatsappIds: number[]
 *  - onChange: (ids: number[]) => void
 *  - disabled?: boolean
 *  - label?: string  (opcional, default "Conexões permitidas")
 */
const useStyles = makeStyles((theme) => ({
  chips: {
    display: "flex",
    flexWrap: "nowrap",
    gap: 4,
    alignItems: "center",
    overflow: "hidden",
  },
  chip: {
    margin: 0,
    maxWidth: 110,
    flexShrink: 1,
    "& .MuiChip-label": {
      padding: "0 6px",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      display: "block",
    },
  },
  chipCounter: {
    margin: 0,
    minWidth: 30,
    flexShrink: 0,
    "& .MuiChip-label": {
      padding: "0 4px",
    },
  },
  select: {
    height: 40,
    "& .MuiOutlinedInput-notchedOutline": {
      borderRadius: 10,
    },
  },
  channelHint: {
    fontSize: "0.7rem",
    color: theme.palette.text.disabled,
    marginLeft: 6,
  },
}));

const channelLabel = (channel) => {
  if (!channel) return "";
  const c = String(channel).toLowerCase();
  if (c.includes("waba")) return "WABA";
  if (c.includes("wuzapi")) return "Wuzapi";
  if (c === "whatsmeow") return "WhatsApp";
  if (c === "whatsapp") return "WhatsApp";
  if (c === "facebook") return "Facebook";
  if (c === "instagram") return "Instagram";
  if (c === "telegram") return "Telegram";
  if (c === "webchat") return "Webchat";
  return channel;
};

const WhatsappSelect = ({
  selectedWhatsappIds = [],
  onChange,
  disabled = false,
  label = "Conexões permitidas",
}) => {
  const classes = useStyles();
  const [whatsapps, setWhatsapps] = useState([]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/whatsapp/", { params: { session: 0 } });
        if (!mounted) return;
        // Excluir canal de email (mesma regra do backend para não confundir
        // o admin com conexões que não criam tickets WhatsApp/canal).
        const filtered = Array.isArray(data)
          ? data.filter((w) => String(w.channel || "").toLowerCase() !== "email")
          : [];
        setWhatsapps(filtered);
      } catch (err) {
        toastError(err);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const handleChange = (e) => {
    onChange(e.target.value);
  };

  return (
    <Box>
      <FormControl fullWidth margin="dense" variant="outlined" size="small">
        <InputLabel>{label}</InputLabel>
        <Select
          multiple
          labelWidth={120}
          value={selectedWhatsappIds || []}
          onChange={handleChange}
          disabled={disabled}
          size="small"
          className={classes.select}
          MenuProps={{
            anchorOrigin: { vertical: "bottom", horizontal: "left" },
            transformOrigin: { vertical: "top", horizontal: "left" },
            getContentAnchorEl: null,
          }}
          renderValue={(selected) => {
            if (!selected || selected.length === 0) return null;

            const firstWhatsapp = whatsapps.find((w) => w.id === selected[0]);
            if (!firstWhatsapp) return null;

            const remainingCount = selected.length - 1;

            return (
              <div className={classes.chips}>
                <Tooltip title={firstWhatsapp.name} arrow placement="top">
                  <Chip
                    variant="outlined"
                    label={firstWhatsapp.name}
                    size="small"
                    className={classes.chip}
                  />
                </Tooltip>
                {remainingCount > 0 && (
                  <Tooltip
                    title={`${remainingCount} ${
                      remainingCount === 1
                        ? "conexão adicional"
                        : "conexões adicionais"
                    }`}
                    arrow
                    placement="top"
                  >
                    <Chip
                      variant="outlined"
                      label={`+${remainingCount}`}
                      size="small"
                      className={classes.chipCounter}
                    />
                  </Tooltip>
                )}
              </div>
            );
          }}
        >
          {whatsapps.map((whatsapp) => (
            <MenuItem key={whatsapp.id} value={whatsapp.id}>
              {whatsapp.name}
              <span className={classes.channelHint}>
                {channelLabel(whatsapp.channel)}
              </span>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default WhatsappSelect;
